import { useState, useCallback } from 'react';
import { supabase } from '@/lib/supabase/client';
import { toast } from '@/components/ui/use-toast';

export interface JobCardQuantities {
  good: number;
  scrap: number;
  rework: number;
}

export interface DefectLog {
  id: string;
  job_card_id: string;
  defect_reason: string;
  quantity: number;
  description?: string;
  corrective_action?: string;
  created_by: string;
  created_at: string;
}

export const useJobCardQuantities = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Save quantities with validation and business logic
  const saveQuantities = useCallback(async (
    jobCardId: string, 
    operatorId: string, 
    quantities: JobCardQuantities,
    defectReasons?: Array<{ reason: string; quantity: number; description?: string }>
  ) => {
    setLoading(true);
    setError(null);

    try {
      // Debug session and user context
      const { data: { session } } = await supabase.auth.getSession();
      console.log('Current session:', session);
      console.log('Operator ID being used:', operatorId);
      console.log('Session user ID:', session?.user?.id);

      // 1. Client Validation - Good + Scrap + Rework ≤ Job order qty
      const { data: jobCard, error: fetchError } = await supabase
        .from('job_cards')
        .select('id, quantity, status, work_order_operation_id')
        .eq('id', jobCardId)
        .single();

      if (fetchError) throw fetchError;

      const totalQuantity = quantities.good + quantities.scrap + quantities.rework;
      if (totalQuantity > jobCard.quantity) {
        throw new Error(`Total quantity (${totalQuantity}) cannot exceed job order quantity (${jobCard.quantity})`);
      }

      if (totalQuantity <= 0) {
        throw new Error('At least one quantity must be greater than 0');
      }

      const now = new Date().toISOString();

      // 2. Server Transaction - Update job_card quantities
      const { error: updateError } = await supabase
        .from('job_cards')
        .update({
          quantity_completed: quantities.good,
          scrap_quantity: quantities.scrap,
          rework_quantity: quantities.rework,
          updated_at: now,
        })
        .eq('id', jobCardId);

      if (updateError) throw updateError;

      // 3. Create job card activity for quantity update
      const { error: activityError } = await supabase
        .from('job_card_activities')
        .insert({
          job_card_id: jobCardId,
          user_id: operatorId,
          activity_type: 'edit',
          description: `Quantities updated: Good: ${quantities.good}, Scrap: ${quantities.scrap}, Rework: ${quantities.rework}`,
          quantity: totalQuantity,
          metadata: {
            timestamp: now,
            quantities: quantities,
            operator_id: operatorId,
          }
        });

      if (activityError) throw activityError;

      // 4. If scrap_qty > 0 ⇒ create DefectLog rows per defect reason
      if (quantities.scrap > 0 && defectReasons && defectReasons.length > 0) {
        const defectLogEntries = defectReasons.map(defect => ({
          job_card_id: jobCardId,
          user_id: operatorId,
          activity_type: 'issue',
          description: `Defect logged: ${defect.reason} - Qty: ${defect.quantity}`,
          quantity: defect.quantity,
          metadata: {
            timestamp: now,
            defect_reason: defect.reason,
            defect_description: defect.description || '',
            scrap_quantity: defect.quantity,
          }
        }));

        const { error: defectError } = await supabase
          .from('job_card_activities')
          .insert(defectLogEntries);

        if (defectError) throw defectError;

        // Check if scrap % > threshold for auto NCR creation
        const scrapPercentage = (quantities.scrap / jobCard.quantity) * 100;
        const NCR_THRESHOLD = 10; // 10% scrap rate threshold

        if (scrapPercentage > NCR_THRESHOLD) {
          console.log('Event: ncr.auto_create', { 
            job_id: jobCardId, 
            scrap_percentage: scrapPercentage,
            threshold: NCR_THRESHOLD 
          });
          
          // TODO: Implement NCR auto-creation when NCR module is available
          toast({
            title: 'High Scrap Rate Alert',
            description: `Scrap rate (${scrapPercentage.toFixed(1)}%) exceeds threshold. Consider creating an NCR.`,
            variant: 'destructive',
          });
        }
      }

      // 5. Create/update MaterialBackflush rows if configured (placeholder for future)
      // TODO: Implement back-flush material logic when routing configuration is available
      
      // 6. Update work order progress calculation
      await updateWorkOrderProgress(jobCard.work_order_operation_id);

      // 7. Event Chain
      console.log('Event: quantities.saved', { 
        job_id: jobCardId, 
        operator_id: operatorId, 
        quantities,
        timestamp: now 
      });

      toast({
        title: 'Quantities Saved',
        description: `Good: ${quantities.good}, Scrap: ${quantities.scrap}, Rework: ${quantities.rework}`,
      });

      return { success: true, quantities };

    } catch (error: any) {
      console.error('Error saving quantities:', error);
      setError(error.message);
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  }, []);

  // Helper function to update work order progress
  const updateWorkOrderProgress = async (workOrderOperationId: string) => {
    try {
      // Get all job cards for this work order operation
      const { data: jobCards } = await supabase
        .from('job_cards')
        .select('quantity, quantity_completed, scrap_quantity')
        .eq('work_order_operation_id', workOrderOperationId);

      if (jobCards && jobCards.length > 0) {
        const totalPlanned = jobCards.reduce((sum, jc) => sum + jc.quantity, 0);
        const totalCompleted = jobCards.reduce((sum, jc) => sum + (jc.quantity_completed || 0), 0);
        const totalScrap = jobCards.reduce((sum, jc) => sum + (jc.scrap_quantity || 0), 0);
        
        const progressPercentage = totalPlanned > 0 ? ((totalCompleted + totalScrap) / totalPlanned) * 100 : 0;

        // Update work order operation with progress
        await supabase
          .from('work_order_operations')
          .update({
            // Note: progress field doesn't exist in schema, using notes field for now
            notes: `Progress: ${progressPercentage.toFixed(1)}% (${totalCompleted} good, ${totalScrap} scrap of ${totalPlanned} planned)`,
            updated_at: new Date().toISOString(),
          })
          .eq('id', workOrderOperationId);

        // Event for work order progress update
        console.log('Event: wo.progress_update', { 
          operation_id: workOrderOperationId, 
          progress_percentage: progressPercentage,
          total_completed: totalCompleted,
          total_scrap: totalScrap,
          total_planned: totalPlanned
        });
      }
    } catch (error) {
      console.error('Error updating work order progress:', error);
    }
  };

  // Get defect logs for a job card
  const getDefectLogs = useCallback(async (jobCardId: string) => {
    try {
      const { data, error } = await supabase
        .from('job_card_activities')
        .select(`
          id,
          description,
          quantity,
          metadata,
          created_at,
          user_id
        `)
        .eq('job_card_id', jobCardId)
        .eq('activity_type', 'defect_logged')
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // Extract unique user IDs from the defect logs
      const userIds = [...new Set((data || []).map((item: any) => item.user_id).filter(Boolean))];
      
      // Fetch user data from public.users table
      let userData: any[] = [];
      if (userIds.length > 0) {
        const { data: users, error: userError } = await supabase
          .from('users')
          .select('id, email, full_name')
          .in('id', userIds);
        
        if (userError) {
          console.error('Error fetching user data:', userError);
        } else {
          userData = users || [];
        }
      }
      
      // Create a map of user data for quick lookup
      const userMap = new Map(userData.map(user => [user.id, user]));
      
      // Add user information to each defect log
      const defectLogsWithUsers = (data || []).map((item: any) => {
        const user = userMap.get(item.user_id);
        
        // Extract user name from full_name if available
        let userName = 'Unknown User';
        if (user?.full_name) {
          userName = user.full_name;
        } else if (user?.email) {
          userName = user.email;
        }
        
        return {
          ...item,
          user_name: userName
        };
      });
      
      return { data: defectLogsWithUsers, error: null };
    } catch (error: any) {
      console.error('Error fetching defect logs:', error);
      return { data: null, error: error.message };
    }
  }, []);

  // Validate quantity inputs
  const validateQuantities = (quantities: JobCardQuantities, maxQuantity: number): string | null => {
    if (quantities.good < 0 || quantities.scrap < 0 || quantities.rework < 0) {
      return 'Quantities cannot be negative';
    }

    const total = quantities.good + quantities.scrap + quantities.rework;
    if (total > maxQuantity) {
      return `Total quantity (${total}) cannot exceed job order quantity (${maxQuantity})`;
    }

    if (total === 0) {
      return 'At least one quantity must be greater than 0';
    }

    return null;
  };

  return {
    saveQuantities,
    getDefectLogs,
    validateQuantities,
    loading,
    error,
  };
};
